// Copyright 2019 Fowlit. All rights reserved.
//
// Use of this source code is governed by the MIT license that can be found in
// the LICENSE.md file.

// SearchBar component with dropdopwn list.

import React from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { Colors, Fonts, Dimensions, Media } from 'fowlit';
import { useComponentVisible } from 'Hooks';

const selectHeight = '50px';
const itemHeight = '50px';

const Container = styled.div`
  user-select: none;
  position: relative;
  padding: 0 10px;
  border-radius: 5px;
  background: ${props => props.theme.body};
  border: 1px solid ${props => props.theme.border};
  border-top-left-radius: 5px;
  border-top-right-radius: 5px;
  border-bottom-left-radius: ${props => (props.active ? 0 : '5px')};
  border-bottom-right-radius: ${props => (props.active ? 0 : '5px')};
  color: ${props => props.theme.color};
  display: grid;
  box-sizing: border-box;
  grid-template-columns: 1fr;
  grid-template-rows: ${selectHeight} 1fr;
  grid-template-areas: 'label' 'dropdown';
  justify-content: space-between;
  align-items: center;
  position: relative;
  z-index: 1000;
  outline: none;
  width: 100%;
`;

const Label = styled.div`
  grid-area: label;
  color: ${props => props.theme.color};
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-family: ${Fonts.text};
  font-size: ${Fonts.sizeSuperTiny};
  cursor: pointer;

  i {
    transform: ${props => (props.active ? `rotateZ(180deg)` : `none`)};
    transition: 200ms ease-in-out;
    font-size: ${Fonts.sizeTiny};
  }
`;

const Dropdown = styled.div`
  background: ${props => props.theme.body};
  display: ${props => (props.active ? 'block' : 'none')};
  position: absolute;
  top: ${selectHeight};
  left: -1px;
  right: -1px;
  box-shadow: 1px 5px 5px -7px ${Colors.black};
  border-bottom-left-radius: ${Dimensions.radius};
  border-bottom-right-radius: ${Dimensions.radius};
  border-left: 1px solid ${props => props.theme.border};
  border-right: 1px solid ${props => props.theme.border};
  border-bottom: 1px solid ${props => props.theme.border};

  @media (max-width: ${Media.phone}) {
    grid-area: dropdown;
    top: -2px;
  }
`;

const Item = styled.div`
  line-height: ${itemHeight};
  padding: 0 10px;
  font-size: ${Fonts.sizeSuperTiny};
  font-family: ${Fonts.text};
  background: ${props => (props.selected ? props.theme.main : 'none')};
  color: ${props => props.theme.color};
  cursor: pointer;
  display: flex;
  justify-content: space-between;

  &:hover {
    background: ${props => props.theme.main};
  }
  &:last-of-type {
    border-bottom-left-radius: ${Dimensions.radius};
    border-bottom-right-radius: ${Dimensions.radius};
  }
`;

const Select = ({ label, options, value, setValue }) => {
  const { ref, visible, setVisible } = useComponentVisible(false);

  return (
    <Container active={visible} ref={ref} tabIndex={0}>
      <Label active={visible} onClick={() => setVisible(v => !v)}>
        {value.type === '' ? label : value.label}
        <i className="icon-angle-down" />
      </Label>
      <Dropdown active={visible}>
        {options.map(e => (
          <Item
            key={e.type}
            selected={e.type === value.type}
            onClick={() => {
              if (e.type !== value.type) {
                setValue(e);
                setVisible(false);
              } else {
                setValue({ type: '', label: '' });
              }
            }}
          >
            {e.label}
            <i className={e.type === value.type ? 'icon-cancel-circled' : ''} />
          </Item>
        ))}
      </Dropdown>
    </Container>
  );
};

Select.propTypes = {
  label: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string
    })
  ).isRequired,
  value: PropTypes.shape({
    type: PropTypes.string,
    label: PropTypes.string
  }).isRequired,
  setValue: PropTypes.func.isRequired
};

export default Select;
